<template>
  <div class="stats-panel">
    <Loading v-if="isLoading" class="loading"></Loading>
    <slot name="empty" v-else-if="shouldDisplayEmpty"></slot>
    <template v-else>
      <ContentBlock class="subheader">
        <div class="filters" v-if="!isWaitingPopulation">
          <div class="periodselectcontainer w-100">
            <h6>{{ periodType === 'month' ? $t('Month') : $t('Year')}}</h6>
            <UISelect class="periodselect" v-model="periodValue">
              <option v-for="({ key, value }) in periodOptions" :value="key" :key="key">{{ value }}</option>
            </UISelect>
          </div>
          <div class="periodswitch w-100">
            <UIButton
              class="periodradio f4 flex-auto w-50"
              :ghost="true"
              name="period"
              :class="{active: periodType === 'month'}"
              @click="periodType = 'month'"
            >{{ $t('Month') }}</UIButton>
            <UIButton
              class="periodradio f4 flex-auto w-50"
              :ghost="true"
              name="period"
              :class="{active: periodType === 'year'}"
              @click="periodType = 'year'"
            >{{ $t('Year') }}</UIButton>
          </div>
        </div>
      </ContentBlock>
      <div class="wrapper" v-if="periodType">
        <GameStats
          v-for="(stat, i) in developerStats"
          :type="periodType"
          :stat="stat"
          :is-current-date="isCurrentDate"
          :key="stat.game.id"
        >
          <span
            class="f6"
            slot="additional"
            v-if="developerStats && i === developerStats.length - 1"
          >*{{ $t('This is an estimation and does not include any service fees.')}}</span>
        </GameStats>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import GameStats from './StatsPanel/GameStats';
import Component from 'vue-class-component';
import UIButton from '@/core/shared/components/ui/UIButton';
import ContentBlock from '@/core/shared/components/ContentBlock';
import Loading from '@/core/shared/components/Loading';
import moment from 'moment';
import UISelect from '@/core/shared/components/ui/UISelect';
const nowMoment = moment();

@Component({
  components: {
    GameStats,
    UIButton,
    Loading,
    ContentBlock,
    UISelect,
  },
})
export default class Stats extends Vue {
  periodType = 'month'
  periodValue = null
  isFetchingData = false
  isWaitingPopulation = false
  get developerStats () { return this.$store.state.stats.developerStats; }
  get periodOptions () { return this.$store.state.stats.options[this.periodType]; }
  get isCurrentDate () {
    let format;
    if (this.periodType === 'month') format = 'YYYY-MM';
    else if (this.periodType === 'year') format = 'YYYY';
    const selectedMoment = moment(this.periodValue, format);

    return Boolean(this.periodValue) && nowMoment.isSame(selectedMoment, this.periodType);
  }

  get shouldDisplayEmpty () {
    if (this.periodOptions) {
      return Object.values(this.periodOptions).every(arr => arr.length);
    }
  }

  get isLoading () {
    return this.isFetchingData || this.isWaitingPopulation;
  }

  async created () {
    this.$store.commit('setDeveloperStats', []);
    await this.populateFilterOptions();
    this.$watch(() => this.periodType, this.setDefaultValueInSelect, { immediate: true });
    this.$watch(() => this.periodValue, this.fetchData, { immediate: true });
  }

  setDefaultValueInSelect () {
    if (this.periodOptions && !this.periodOptions.length) return;
    if (this.periodOptions) this.periodValue = this.periodOptions[0].key;
  }

  async fetchData () {
    if (!this.periodValue) return;
    this.isFetchingData = true;
    await this.$store.dispatch('getDeveloperStats', { type: this.periodType, value: this.periodValue });
    this.isFetchingData = false;
  }

  async populateFilterOptions () {
    this.isWaitingPopulation = true;
    await Promise.all([
      this.$store.dispatch('getDeveloperStatIncrements', { type: 'year' }),
      this.$store.dispatch('getDeveloperStatIncrements', { type: 'month' }),
    ]);
    this.isWaitingPopulation = false;
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.stats-panel {
  & >>> .instructions {
    @media (--desktop) {
      flex: 0 0 20rem;
      margin-right: var(--spacingSm);
    }
  }
  & .subheader >>> .content {
    background: none;
    padding: 0;
  }
  & .subheader >>> .panel {
    background: none;
    box-shadow: none;
  }
  & h6 {
    @apply --f7;
  }
  & select {
    margin: 0;
  }
  & .lastupdate {
    @apply --f8;
    margin: 0;
  }
  & .lastupdatecontainer,
  & .periodswitch {
    align-self: end;
  }
  & .lastupdatecontainer {
    grid-column: 1 / span 2;
    grid-row: 2;
    @media (--tablet) {
      grid-row: auto;
      grid-column: auto;
    }
  }
  & .loading {
    text-align: center;
  }
  & .periodswitch {
    display: flex;
    align-self: end;
    & > *:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    & > * + * {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    & label {
      padding: var(--spacingSm);
      @media (--tablet) {
        padding: var(--spacingSm) var(--spacingMd);
      }
    }
  }
  & .periodradio {
    display: block;
    margin: 0;
    text-transform: none;
  }
  & .filters {
    display: grid;
    grid-gap: var(--spacingSm);
    justify-content: end;
    justify-items: end;
    grid-template-columns: repeat(2, 1fr);
    @media (--tablet) {
      grid-gap: var(--spacingMd);
      grid-template-columns: repeat(2, minmax(min-content, 30%));
      & .periodselectcontainer {
        min-width: 20rem;
      }
    }
  }
}
</style>
